import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { recordAuthEvent } from "../../utils/authJournal";
import { useAuth } from "../../contexts/AuthContext";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import useAuthorizedModules from "../../hooks/useAuthorizedModules";

/**
 * Componente para proteger rutas que requieren autenticación
 * @param {Object} props Propiedades del componente
 * @param {React.ReactNode} props.children Componentes hijos a renderizar si hay autenticación
 * @param {string} [props.requiredRole] Rol requerido para acceder a la ruta
 * @param {string} [props.requiredModule] Módulo requerido para acceder a la ruta
 * @param {string} [props.redirectTo='/login'] Ruta a la que redirigir si no hay autenticación
 */
const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredModule = null,
  redirectTo = "/firebase-login",
}) => {
  const location = useLocation();
  const { currentUser, role, loading, userInfo, activeOrganization } = useAuth();
  const { isModuleAuthorized, loading: modulesLoading } = useAuthorizedModules();
  
  // Use centralized useEffectiveRole hook for consistency
  const { effectiveRole, isSuperUser } = useEffectiveRole();
  
  console.log("=== PROTECTED ROUTE DIAGNÓSTICO ===");
  console.log("Ruta actual:", location.pathname);
  console.log("Usuario actual:", currentUser?.email);
  console.log("Rol desde contexto:", role);
  console.log("Effective role:", effectiveRole);
  console.log("¿Es super_user?:", isSuperUser);
  console.log("Rol requerido:", requiredRole);
  console.log("Módulo requerido:", requiredModule);
  
  // Verificar acceso basado en rol
  const hasRequiredRole = () => {
    // Si no se requiere un rol específico, permitir acceso
    if (!requiredRole) {
      console.log("No se requiere un rol específico, permitiendo acceso");
      return true;
    }
    
    // SuperUsuario siempre tiene acceso
    if (isSuperUser) {
      console.log("El usuario es super_user, permitiendo acceso");
      return true;
    }
    
    // Si requiredRole es un array, verificar si el rol actual está en el array
    if (Array.isArray(requiredRole)) {
      console.log(`Verificando rol ${effectiveRole} contra array de roles:`, requiredRole);
      const isInArray = requiredRole.includes(effectiveRole);
      console.log(`El rol ${effectiveRole} ${isInArray ? 'está' : 'no está'} en el array de roles permitidos`);
      return isInArray;
    }
    
    // Admin tiene acceso a rutas de admin y user
    if (effectiveRole === 'admin' && ['admin', 'user'].includes(requiredRole)) {
      console.log(`El usuario es admin y requiredRole es ${requiredRole}, permitiendo acceso`);
      return true;
    }
    
    // Usuario normal solo tiene acceso a rutas de usuario
    const result = requiredRole === 'user' && effectiveRole === 'user';
    console.log(`Verificando si usuario (${effectiveRole}) puede acceder a ruta de tipo ${requiredRole}: ${result}`);
    return result;
  };

  // Solo mostrar loading si realmente estamos cargando Y tenemos un usuario autenticado
  // Si no hay usuario, el loading puede ser porque aún no se ha autenticado (normal)
  // Para super_user, no bloquear por modulesLoading ya que tienen acceso total
  // Si modulesLoading es true pero no hay email aún, no bloquear (el email llegará pronto)
  const hasUser = !!currentUser;
  const shouldBlockOnModules = modulesLoading && hasUser && !isSuperUser;
  const shouldShowLoading = loading || shouldBlockOnModules;
  
  if (shouldShowLoading) {
    console.log("Estado de autenticación o módulos aún cargando...", { 
      loading, 
      modulesLoading, 
      currentUser: !!currentUser, 
      role,
      isSuperUser,
      shouldBlockOnModules,
      shouldShowLoading
    });
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Si no hay usuario autenticado, redirigir al login
  if (!currentUser) {
    console.log("No hay usuario autenticado, redirigiendo a", redirectTo);
    recordAuthEvent("protected-route", { reason: "no-user", user: false, withStack: false });
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Access hardening: authenticated users must have an explicit effective role.
  // Without a role assignment, the account is considered pending provisioning.
  if (!effectiveRole) {
    console.warn("⚠️ [ProtectedRoute] Usuario autenticado sin rol efectivo. Bloqueando acceso.");
    recordAuthEvent("protected-route", { reason: "no-role", user: true, role: false, withStack: false });
    return <Navigate to="/firebase-login?access-denied=true" replace />;
  }

  // 🔍 Verificar si usuario necesita completar onboarding
  // IMPORTANTE: Super users NUNCA necesitan onboarding - saltar verificación
  // Solo verificar si NO estamos ya en la ruta de onboarding
  const currentPath = location.pathname;
  if (!currentPath.includes('/onboarding') && userInfo?.user) {
    // Super users siempre tienen onboarding_completed = TRUE y no necesitan verificación
    const isSuperUser = effectiveRole === 'super_user';
    
    if (!isSuperUser) {
      const onboardingCompleted = userInfo.user.onboarding_completed ?? false;
      const hasOrganization = userInfo.user.organization_id && userInfo.user.organization_id > 0;
      
      if (!onboardingCompleted || !hasOrganization) {
        console.log(`⚠️ [ProtectedRoute] Usuario necesita completar onboarding. Redirigiendo...`);
        console.log(`   - onboarding_completed: ${onboardingCompleted}`);
        console.log(`   - has_organization: ${hasOrganization}`);
        return <Navigate to="/onboarding" replace />;
      }
    } else {
      console.log(`✅ [ProtectedRoute] Super user detectado - saltando verificación de onboarding`);
    }
  }

  // Verificar permisos de rol y módulo
  const hasRoleAccess = hasRequiredRole();
  
  // Super usuario tiene acceso a TODOS los módulos sin verificar organización
  // Solo verificar módulo si NO es super_user
  const hasModuleAccess = !requiredModule || 
                          isSuperUser || 
                          isModuleAuthorized(requiredModule);
  
  const hasAccess = hasRoleAccess && hasModuleAccess;
  
  console.log("Resultado de verificación de acceso:");
  console.log("- Tiene rol necesario:", hasRoleAccess);
  console.log("- ¿Es super_user?:", isSuperUser);
  console.log("- Tiene acceso al módulo:", hasModuleAccess, requiredModule ? `(módulo requerido: ${requiredModule})` : '(sin módulo requerido)');
  console.log("- Acceso total:", hasAccess);
  
  // Si no tiene permisos, redirigir al dashboard
  if (!hasAccess) {
    const reason = !hasRoleAccess ? 'Rol incorrecto' : 'Módulo no autorizado';
    console.log(`No tiene permisos suficientes, redirigiendo al dashboard. Motivo: ${reason}`);
    recordAuthEvent("protected-route", { reason: !hasRoleAccess ? "role" : "module", user: true, role: !!effectiveRole, withStack: false });
    console.log(`  - Rol efectivo: ${effectiveRole}`);
    console.log(`  - Rol requerido: ${requiredRole || 'ninguno'}`);
    console.log(`  - Módulo requerido: ${requiredModule || 'ninguno'}`);
    return <Navigate to="/dashboard" replace />;
  }

  console.log("Verificación completa: Tiene acceso. Renderizando componente protegido.");
  // Si pasó todas las verificaciones, renderizar los hijos
  return children;
};

export default ProtectedRoute;
